import { addHours, isAfter, isBefore, parseISO } from "date-fns"
import type { AdvertisementRecord } from "../store/advertisement-atoms"

/**
 * 生成广告内容的哈希值，用于检测广告是否已更新
 * @param advertisement 广告配置
 * @returns string 内容哈希
 */
export function generateAdvertisementContentHash(
	advertisement: AdvertisementConfig | undefined,
): string {
	if (!advertisement) {
		return ""
	}

	// 创建包含关键内容的对象
	const contentData = {
		id: advertisement.id,
		type: advertisement.type,
		image: advertisement.data?.image,
		link: advertisement.data?.link,
		altText: advertisement.data?.altText,
		startDate: advertisement.startDate || advertisement.data?.startDate,
		endDate: advertisement.endDate || advertisement.data?.endDate,
		enabled: advertisement.enabled,
		areas: advertisement.areas,
	}

	// 使用简单的哈希算法（在实际项目中可能需要更复杂的哈希）
	return JSON.stringify(contentData)
}

// 广告配置接口
export interface AdvertisementConfig {
	id: string
	type: string
	data: {
		image?: any
		link?: any
		platform?: "all" | "web" | "app"
		frequency?: "once" | "24h" | "72h"
		userType?: "all" | "new" | "returning"
		altText?: string
		startDate?: string // 广告开始时间（可能在data中）
		endDate?: string // 广告结束时间（可能在data中）
	}
	enabled?: boolean
	areas?: string[]
	startDate?: string // 广告开始时间（根级别）
	endDate?: string // 广告结束时间（根级别）
}

/**
 * 检查广告是否应该显示（基础版本）
 * @param advertisement 广告配置
 * @param currentPlatform 当前平台 ('web' | 'app')
 * @param area 当前地区
 * @returns boolean 是否应该显示广告
 */
export function shouldShowAdvertisement(
	advertisement: AdvertisementConfig | undefined,
	currentPlatform: "web" | "app" = "web",
	area?: string,
): boolean {
	// 基础检查
	if (!advertisement || !advertisement.enabled) {
		return false
	}

	// 时间范围检查
	const currentTime = new Date()

	// 检查根级别的时间设置
	const rootStartDate =
		advertisement.startDate && typeof advertisement.startDate === "string"
			? parseISO(advertisement.startDate)
			: null
	const rootEndDate =
		advertisement.endDate && typeof advertisement.endDate === "string"
			? parseISO(advertisement.endDate)
			: null

	// 检查data中的时间设置
	const dataStartDate =
		advertisement.data?.startDate &&
		typeof advertisement.data.startDate === "string"
			? parseISO(advertisement.data.startDate)
			: null
	const dataEndDate =
		advertisement.data?.endDate &&
		typeof advertisement.data.endDate === "string"
			? parseISO(advertisement.data.endDate)
			: null

	// 使用优先级：data中的时间 > 根级别的时间
	const startDate = dataStartDate || rootStartDate
	const endDate = dataEndDate || rootEndDate

	// 如果设置了开始时间，检查是否已到开始时间
	if (startDate && isBefore(currentTime, startDate)) {
		return false
	}

	// 如果设置了结束时间，检查是否已过结束时间
	if (endDate && isAfter(currentTime, endDate)) {
		return false
	}

	// 地区检查
	if (advertisement.areas && advertisement.areas.length > 0) {
		if (!area || !advertisement.areas.includes(area.toUpperCase())) {
			return false
		}
	}

	// 渠道检查
	const platform = advertisement.data?.platform || "all"
	if (platform !== "all" && platform !== currentPlatform) {
		return false
	}

	return true
}

/**
 * 检查用户类型是否匹配（同步版本）
 * @param userType 广告设置的用户类型
 * @param hasSession 用户是否已登录
 * @param isNewUser 用户是否为新用户
 * @returns boolean 是否匹配
 */
export function checkUserTypeMatch(
	userType: "all" | "new" | "returning",
	hasSession: boolean,
	isNewUser: boolean = false,
): boolean {
	// 如果用户类型是 'all'，则所有用户都可以看到（包括未登录用户）
	if (userType === "all") {
		return true
	}

	// 对于特定用户类型，必须已登录
	if (!hasSession) {
		return false
	}

	// 已登录用户，根据用户类型判断
	if (userType === "new") {
		return isNewUser
	}

	if (userType === "returning") {
		return !isNewUser
	}

	return false
}

/**
 * 检查是否在首页
 * @param pathname 当前路径
 * @returns boolean 是否在首页
 */
export function isHomePage(pathname: string | undefined): boolean {
	if (!pathname) {
		return false
	}

	// 判断是否只有locale部分（首页）
	// 路径格式: /locale 或 /locale/，其中locale可以是 en, en-GB, en-us-GB 等
	// 如果路径中有第三个斜杠，说明不是首页
	const pathSegments = pathname.split("/").filter((segment) => segment !== "")
	return pathSegments.length <= 1 // 只有locale部分，没有其他路径
}

/**
 * 统一的广告显示校验方法（包含首页检查）
 * 检查所有条件：首页、启用状态、时间、地区、渠道、用户类型、频率
 * @param advertisement 广告配置
 * @param platform 当前平台
 * @param area 当前地区
 * @param pathname 当前路径
 * @param userType 广告设置的用户类型
 * @param hasSession 用户是否已登录
 * @param isNewUser 用户是否为新用户
 * @param frequencyRecords 频率记录
 * @returns boolean 是否应该显示广告
 */
export function shouldShowAdvertisementComplete(
	advertisement: AdvertisementConfig | undefined,
	platform: "web" | "app",
	area: string | undefined,
	pathname: string | undefined,
	userType: "all" | "new" | "returning" | undefined,
	hasSession: boolean,
	isNewUser: boolean = false,
	frequencyRecords?: Record<string, AdvertisementRecord>,
): boolean {
	// 0. 首页检查
	if (!isHomePage(pathname)) {
		return false
	}

	// 1. 基础检查（启用状态、时间、地区、渠道）
	if (!shouldShowAdvertisement(advertisement, platform, area)) {
		return false
	}

	// 2. 用户类型检查
	if (userType && !checkUserTypeMatch(userType, hasSession, isNewUser)) {
		return false
	}

	// 3. 频率检查
	if (
		frequencyRecords &&
		!canShowBasedOnFrequency(advertisement, frequencyRecords, area)
	) {
		return false
	}

	return true
}

/**
 * 检查频率控制（支持地区隔离和内容变更检测）
 * @param advertisement 广告配置
 * @param frequencyRecords 频率记录
 * @param area 当前地区
 * @returns boolean 是否满足频率要求
 */
export function canShowBasedOnFrequency(
	advertisement: AdvertisementConfig | undefined,
	frequencyRecords: Record<string, AdvertisementRecord>,
	area: string | undefined,
): boolean {
	if (!advertisement || !area) {
		return false
	}

	const frequency = advertisement.data?.frequency
	if (!frequency) {
		return true // 没有设置频率限制，默认允许
	}

	const cacheKey = `${advertisement.id}_${area}`
	const record = frequencyRecords[cacheKey]

	// 首次显示
	if (!record) {
		return true
	}

	// 检查广告内容是否已更新
	const currentContentHash = generateAdvertisementContentHash(advertisement)
	const storedContentHash = record.contentHash

	// 如果内容哈希不同，说明广告已更新，应该显示新广告
	if (storedContentHash && currentContentHash !== storedContentHash) {
		return true
	}

	// 有记录的情况下，检查频率限制
	if (frequency === "once") {
		return false
	}

	// 时间限制检查（24h, 72h）
	const lastShownTime = new Date(record.lastShown)
	const currentTime = new Date()

	let duration: Date
	switch (frequency) {
		case "24h":
			duration = addHours(lastShownTime, 24)
			break
		case "72h":
			duration = addHours(lastShownTime, 72)
			break
		default:
			return true // 未知频率，默认允许
	}

	const canShow = isAfter(currentTime, duration)

	return canShow
}
